import { css } from '@emotion/react'
import { Flow, Theme, useTheme, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonBoxField } from 'components/form/field/SimNaoSwitchButtonBoxField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { isEmpty } from 'util/checks'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { PerguntaSimNaoRadioListField } from '../../components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '../../consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import {
  FOCUS_GO_NEXT_BUTTON,
  ScrollableFieldsConfig,
  useConsultaSeguimentoAutoScroll,
} from '../../hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import {
  ConsultaSeguimentoPergunta02IgnoraEnum,
  ConsultaSeguimentoPergunta02Model,
  ConsultaSeguimentoPergunta02ResultadoExameEnum,
} from './model-consultaSeguimentoPergunta02'
import {
  calculateResultadoPergunta02,
  conditionToCalculatePergunta02,
  hasPergunta02IgnoraAnySim,
  isPergunta02IgnoraAllNao,
} from './utils-consultaSeguimentoPergunta02'

interface ConsultaSeguimentoPergunta02FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta02Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta02Field(props: Readonly<ConsultaSeguimentoPergunta02FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta02Model>(resolveName(name), {
    subscription: { value: true },
  })

  const hasAnySim = hasPergunta02IgnoraAnySim(value.PERGUNTA_02_IGNORA)
  const isAllNao = isPergunta02IgnoraAllNao(value.PERGUNTA_02_IGNORA)

  const isPergunta02IgnoraRespondida = hasAnySim || isAllNao

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta02Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta02,
    calculateResultado: calculateResultadoPergunta02,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta02Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_02_SCROLL_CONFIG,
  })

  return (
    <VFlow>
      {isPerguntaStatusRespondendo(value.status) && (
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_02}
          title='“Você informou que você já se perguntou se o seu filho pode ser surdo. O que o levou a esse pensamento?”'
          status={value.status}
        />
      )}
      <VFlow vSpacing={0}>
        <ConsultaSeguimentoIndicatorStart />
        <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta02IgnoraEnum>
          name={name.PERGUNTA_02_IGNORA}
          label='Ele ou ela...'
          options={PERGUNTA_02_IGNORA_OPTIONS}
          required
          onChange={() => triggerScrollFor('PERGUNTA_02_IGNORA')}
        />
        {value.resultado && (
          <ConsultaSeguimentoIndicatorResultado
            text={{
              FALHOU: 'Sim para qualquer um dos dois:',
              PASSOU: 'Não para ambos:',
            }}
            resultado={value.resultado}
          />
        )}
        {isPergunta02IgnoraRespondida && (
          <Flow direction='vertical' gap={0} id={getId('PERGUNTA_02_FEZ_EXAME')}>
            <ConsultaSeguimentoIndicatorStep text='Pergunte para todas as crianças, independente da pontuação:' />
            <VFlow>
              <SimNaoSwitchButtonBoxField
                name={name.PERGUNTA_02_FEZ_EXAME}
                label='“O seu filho fez exame auditivo?”'
                headingLevel={3}
                style={styles.fezExameBox}
                required
                onChange={() => triggerScrollFor('PERGUNTA_02_FEZ_EXAME')}
              />
              <div id={getId('PERGUNTA_02_RESULTADO_EXAME')}>
                <RadioGroupBoxField<ConsultaSeguimentoPergunta02ResultadoExameEnum>
                  name={name.PERGUNTA_02_RESULTADO_EXAME}
                  label='Quais os resultados do teste auditivo?'
                  tooltipText={
                    value?.PERGUNTA_02_FEZ_EXAME !== SimNaoEnum.SIM
                      ? 'Apenas necessário se responder “Sim” na anterior'
                      : undefined
                  }
                  disabled={value?.PERGUNTA_02_FEZ_EXAME !== SimNaoEnum.SIM}
                  options={PERGUNTA_02_RESULTADO_EXAME_OPTIONS}
                  onChange={() => triggerScrollFor('PERGUNTA_02_RESULTADO_EXAME')}
                />
              </div>
            </VFlow>
          </Flow>
        )}
      </VFlow>
    </VFlow>
  )
}

const PERGUNTA_02_IGNORA_OPTIONS: RadioOption<ConsultaSeguimentoPergunta02IgnoraEnum>[] = [
  {
    label: 'Com frequência ignora sons',
    value: ConsultaSeguimentoPergunta02IgnoraEnum.IGNORA_SONS,
  },
  {
    label: 'Com frequência ignora as pessoas',
    value: ConsultaSeguimentoPergunta02IgnoraEnum.IGNORA_PESSOAS,
  },
]

const PERGUNTA_02_RESULTADO_EXAME_OPTIONS: RadioOption<ConsultaSeguimentoPergunta02ResultadoExameEnum>[] = [
  {
    label: 'Audição na faixa normal',
    value: ConsultaSeguimentoPergunta02ResultadoExameEnum.AUDICAO_NORMAL,
  },
  {
    label: 'Audição abaixo do normal',
    value: ConsultaSeguimentoPergunta02ResultadoExameEnum.AUDICAO_ABAIXO,
  },
  {
    label: 'Resultados que não tiveram conclusão ou não definitivos',
    value: ConsultaSeguimentoPergunta02ResultadoExameEnum.NAO_DEFINITIVO,
  },
]

const PERGUNTA_02_SCROLL_CONFIG: ScrollableFieldsConfig<ConsultaSeguimentoPergunta02Model> = {
  PERGUNTA_02_IGNORA: {
    scrollConditions: [
      {
        if: (value) => hasPergunta02IgnoraAnySim(value) || isPergunta02IgnoraAllNao(value),
        scrollTo: 'PERGUNTA_02_FEZ_EXAME',
      },
    ],
  },
  PERGUNTA_02_FEZ_EXAME: {
    scrollConditions: [
      {
        if: (value) => value === SimNaoEnum.SIM,
        scrollTo: 'PERGUNTA_02_RESULTADO_EXAME',
      },
      {
        if: (value) => value === SimNaoEnum.NAO,
        scrollTo: FOCUS_GO_NEXT_BUTTON,
      },
    ],
  },
  PERGUNTA_02_RESULTADO_EXAME: {
    scrollConditions: [
      {
        if: (value) => !isEmpty(value),
        scrollTo: FOCUS_GO_NEXT_BUTTON,
      },
    ],
  },
}

const createStyles = (theme: Theme) => ({
  andaSemApoioLabel: css`
    color: ${theme.pallete.primary.main};
  `,
  fezExameBox: css`
    border-color: ${theme.pallete.gray.c80};
    background-color: ${theme.pallete.gray.c90};
  `,
})
